import { AfterContentInit, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { interval, Subscription } from 'rxjs';
import { BannerNotificationsService, PaginatedList, SpinnerService } from '../../../xform-compat';
import { CapitalAvailable, FundSummary, IndexCompany } from '../../models';
import { FundTypes } from '../../enums/fund-types.enum';
import { PERMISSIONS } from '../../enums/permissions.enum';
import { AuthorizationService } from '../../../core/services';
import { EventsHubService } from '../../../core/events-hub.service';
import { CapitalAvailableService, PerformanceDashboardService } from '../../../core/data-services';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RmsFormatMinMillionMoneyPipe } from '../../pipes/rms-format-min-million-money.pipe';

/**
 * Component class for performance dashboard - fund history
 */
@Component({
    selector: 'rms-performance-fund-summary',
    templateUrl: './performance-fund-summary.component.html',
    styleUrls: ['./performance-fund-summary.component.scss'],
    standalone: false
})
export class PerformanceFundSummaryComponent implements AfterContentInit, OnChanges, OnInit, OnDestroy {
  @Input() intradayData?: PaginatedList<FundSummary>;
  @Input() portfolioId?: number;
  @Input() portfolioType: string;
  private dataTimerSubscription: Subscription;
  private destroy$ = new Subject<boolean>();
  private isComponentAlive = true;
  public intraDaySummaries: Array<FundSummary>;
  public indexCompanies: PaginatedList<IndexCompany>;
  public sortedIndexCompanies: Array<IndexCompany> = [];
  public FUND_TYPES = FundTypes;
  public PERMISSIONS = PERMISSIONS;
  public counterMessage: any;
  public displaySummaryDetails = false;
  public editingCapitalAvailable = false;
  public capitalAvailable: CapitalAvailable = new CapitalAvailable();
  public totalAUMTooltip: string;
  private totalFundValue: number;
  public longAcquiredFundSummary: FundSummary;

  constructor(
    private performanceDashboardService: PerformanceDashboardService,
    private spinnerService: SpinnerService,
    private bannerNotificationService: BannerNotificationsService,
    private eventsHubService: EventsHubService,
    public authorizationService: AuthorizationService,
    private rmsFormatMinMillionMoneyPipe: RmsFormatMinMillionMoneyPipe,
    private capitalAvailableService: CapitalAvailableService
  ) {
    this.rmsFormatMinMillionMoneyPipe = rmsFormatMinMillionMoneyPipe;
  }

  ngOnInit() {
  }

  ngOnChanges(changes: SimpleChanges) {
    // changes.prop contains the old and the new value...
    if (this.intradayData) {
      this.loadData();
    }
  }

  ngAfterContentInit () {
    this.loadData();
  }

  ngOnDestroy () {
    this.isComponentAlive = false;
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  /**
   * Filter for intra summary
   * @param intraData Intra summary data
   * @returns True if the item can be displayed
   */
  private intraFilter(intraData: FundSummary): boolean {
    if (intraData.summaryType === 'LONG_ACQUIRED') {
      return intraData.valueInUsd > 0;
    }
    if (this.portfolioType === 'VENTURE') {
      return intraData.summaryType !== 'COMMITTED_CASH';
    } else {
      return intraData.summaryType !== 'COMMITTED_CASH' && intraData.summaryType !== 'UNFUNDED' && intraData.summaryType !== 'TOTAL';
    }
  }

  /**
   * Loads initial fund summary data and index companies data
   * This is called with the {@Link startTimer} method
   */
  private loadData () {
    this.getTotalFundValue();
    this.getCapitalAvailable();
  }

  private getPortfolioIntradaySummary() {
    if ( this.intradayData ) {
      this.useParentData();
      return;
    }
    const extrafields = ['intradaySummary', 'links'];
    this.counterMessage = '...';

    this.performanceDashboardService.getPortfolioIntraDaySummary(this.portfolioId)
    .subscribe(
      value => {
        this.intraDaySummaries = value.records.filter(item => this.intraFilter(item));
        this.mapIntraDaySummaryNames();

        this.performanceDashboardService.getIndexCompanies({extrafield: extrafields}).subscribe(
            indexCompanies => {
              this.indexCompanies = indexCompanies;
              this._applySortToIndexCompanies();
              this.startTimer();
            },
            error => {
              if (this.isComponentAlive) {
                this.bannerNotificationService.error(error);
                this.startTimer();
              }
            }
          );
        },
        error1 => {
          if (this.isComponentAlive) {
            this.bannerNotificationService.error(error1);
            this.startTimer();
          }
        }
      );
  }

  private getTotalFundValue() {
    const params = {
      summaryTypes: ['AUM'],
      portfolioTypes: ['STANDARD', 'VENTURE'],
    };
    this.performanceDashboardService.getTotalFundValue(params).subscribe(
      value => {
        this.totalFundValue = value.records.reduce((acc, item) => acc + item['valueInUsd'], 0);
        this.totalAUMTooltip = this.setTotalAUMTooltip(this.totalFundValue);
        this.getPortfolioIntradaySummary();
      },
      error => {
        if (this.isComponentAlive) {
          this.bannerNotificationService.error(error);
          this.getPortfolioIntradaySummary();
        }
      }
    );
  }

  private setTotalAUMTooltip(value: number): string {
    return `Total AUM: \$${this.rmsFormatMinMillionMoneyPipe.transform(value)}`;
  }

  /**
   * Uses the data provided by the parent instead of the one of the service to avoid missmatches
   */
  private useParentData(): void {
    const extrafields = ['intradaySummary', 'links'];
    this.counterMessage = '...';
    this.intraDaySummaries = this.intradayData.records.filter(item => this.intraFilter(item));
    this.mapIntraDaySummaryNames();
    this.performanceDashboardService.getIndexCompanies({extrafield: extrafields}).subscribe(
      indexCompanies => {
        this.indexCompanies = indexCompanies;
        this._applySortToIndexCompanies();
      },
      error => {
        if (this.isComponentAlive) {
          this.bannerNotificationService.error(error);
        }
      }
    );
  }

  /**
   * Starts the timer to reload the fund summary data
   * if the timers gets to zero, unsubscribe the counter, reloads the data and subscribes back again
   */
  private startTimer () {
    this.counterMessage = 60;
    this.dataTimerSubscription = interval(1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.counterMessage--;
        if (this.counterMessage < 1) {
          this.eventsHubService.dashboardTimerCompletes();
          this.dataTimerSubscription.unsubscribe();
          this.loadData();
        }
      });
  }

  /**
   * Apply the sort needed to the index companies
   * Remove entries for index company IDs 4 and 5.
   */
  private _applySortToIndexCompanies(): void {
    const records = this.indexCompanies.records;
    const filteredRecords = records.filter(record => record.id !== 4 && record.id !== 5);
    this.sortedIndexCompanies = [filteredRecords[0], filteredRecords[2], filteredRecords[1]];
    this.sortedIndexCompanies[2].name = this.sortedIndexCompanies[2].name.slice(1);
  }

  /**
   * get Capital Available
   */
  private getCapitalAvailable() {
    this.capitalAvailableService.getCapitalAvailable().subscribe(
      value => {
        this.capitalAvailable = value;
      },
      error => {
        this.bannerNotificationService.error(error);
      }
    );
  }

  /**
   * create or update Capital Available
   * @param request
   */
  public createOrUpdateCapitalAvailable(request: CapitalAvailable) {
    request.userUpdate = true;
    this.capitalAvailableService.createOrUpdateCapitalAvailable(request).subscribe(
      value => {
        this.capitalAvailable = value;
      },
      error => this.bannerNotificationService.error(error)
    );
  }

  public editCapitalAvailable(editing: boolean) {
    this.editingCapitalAvailable = editing;
  }

  private mapIntraDaySummaryNames() {
    this.intraDaySummaries.map(item => {
      if (item.summaryType === 'LONG_ACQUIRED' && item.valueInUsd > 0) {
        this.longAcquiredFundSummary = item;
      }
      if (this.portfolioType === 'VENTURE' && item.summaryType === 'TOTAL') {
        item.name = 'NAV';
      } else if (this.portfolioType !== 'VENTURE' && item.summaryType === 'AUM') {
        item.name = this.FUND_TYPES.TOTAL;
      } else {
        item.name = this.FUND_TYPES[item.summaryType];
      }
    });
  }
}
